import { ArrowRight, Clock, Tag, ArrowLeft, Sun, Moon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t, Language } from "@/i18n/translations";
import { insightsSeo } from "@/i18n/seo";
import { articles } from "@/data/articles";
import { Link } from "react-router-dom";
import sentinelIcon from "@/assets/sentinelhub-icon.svg";
import { Helmet } from "react-helmet-async";
import { useTheme } from "@/hooks/useTheme";
import { LANG_HTML, LOCALE, SITE_URL, localizedPath } from "@/lib/routes";

const LANGUAGES: { code: Language; label: string; flag: string; altText: string }[] = [
  { code: "pt", label: "PT", flag: "https://flagcdn.com/w40/br.png", altText: "Selecione o idioma Português" },
  { code: "es", label: "ES", flag: "https://flagcdn.com/w40/es.png", altText: "Selecciona el idioma Español" },
  { code: "en", label: "EN", flag: "https://flagcdn.com/w40/us.png", altText: "Select English language" },
];

const Insights = () => {
  const { lang, setLang } = useLanguage();
  const ins = translations.insights;
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  const pageTitle = t(insightsSeo.title, lang);
  const pageDesc = t(insightsSeo.description, lang);
  const canonical = `${SITE_URL}${localizedPath("insights", lang)}`;
  const homePath = localizedPath("home", lang);

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDark
          ? "bg-[hsl(222,47%,5%)] text-[hsl(210,40%,98%)]"
          : "bg-[hsl(0,0%,98%)] text-[hsl(222,47%,15%)]"
      }`}
    >
      <Helmet>
        <html lang={LANG_HTML[lang]} />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="pt-BR" href={`${SITE_URL}${localizedPath("insights", "pt")}`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}${localizedPath("insights", "en")}`} />
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}${localizedPath("insights", "es")}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${localizedPath("insights", "pt")}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={LOCALE[lang]} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
      </Helmet>


      {/* Header */}
      <header
        className={`sticky top-0 z-50 border-b backdrop-blur-xl transition-colors ${
          isDark
            ? "bg-[hsl(222,47%,5%)]/80 border-[hsl(222,30%,18%)]"
            : "bg-white/80 border-gray-200"
        }`}
      >
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link
                to={homePath}
                aria-label={lang === "en" ? "Back to SentinelHub home" : lang === "es" ? "Volver al inicio de SentinelHub" : "Voltar para a página inicial da SentinelHub"}
                className={`flex items-center gap-1 sm:gap-2 text-sm font-medium transition-colors flex-shrink-0 ${
                  isDark
                    ? "text-[hsl(215,20%,65%)] hover:text-[hsl(210,40%,98%)]"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <div className="flex items-center gap-2 min-w-0">
                <img src={sentinelIcon} alt="SentinelHub Security Platform" className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0" />
                <span className="text-sm font-bold font-display truncate">
                  Sentinel<span className="text-[hsl(197,100%,47%)]">Hub</span>
                  <span className={`hidden sm:inline ml-2 text-xs font-normal ${isDark ? "text-muted-foreground" : "text-muted-foreground"}`}>
                    Insights
                  </span>
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Language - flags only on mobile, hidden in row */}
              <div className={`hidden sm:flex items-center gap-0.5 rounded-lg p-0.5 ${isDark ? "bg-[hsl(222,47%,12%)]" : "bg-gray-100"}`}>
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-all ${
                      lang === l.code
                        ? isDark ? "bg-[hsl(197,100%,47%)]/20 text-[hsl(197,100%,47%)]" : "bg-blue-50 text-blue-600"
                        : isDark ? "text-[hsl(215,20%,65%)] hover:text-[hsl(210,40%,98%)]" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <img src={l.flag} alt={l.altText} className="w-4 h-3 object-cover rounded-sm" />
                    <span className="font-medium">{l.label}</span>
                  </button>
                ))}
              </div>

              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-[hsl(222,47%,12%)] hover:bg-[hsl(222,47%,16%)] text-[hsl(210,40%,98%)]"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
                title={isDark ? t(ins.lightMode, lang) : t(ins.darkMode, lang)}
                aria-label={isDark ? t(ins.lightMode, lang) : t(ins.darkMode, lang)}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Mobile language selector - second row */}
          <div className={`flex sm:hidden items-center justify-center gap-0.5 rounded-lg p-0.5 mt-2 ${isDark ? "bg-[hsl(222,47%,12%)]" : "bg-gray-100"}`}>
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs transition-all ${
                      lang === l.code
                        ? isDark ? "bg-[hsl(197,100%,47%)]/20 text-[hsl(197,100%,47%)]" : "bg-blue-50 text-blue-600"
                        : isDark ? "text-[hsl(215,20%,65%)] hover:text-[hsl(210,40%,98%)]" : "text-muted-foreground hover:text-foreground"
                    }`}
              >
                <img src={l.flag} alt={l.altText} className="w-4 h-3 object-cover rounded-sm" />
                <span className="font-medium">{l.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="py-16">
        {/* Hero */}
        <div className="container mx-auto px-4 text-center mb-16">
          <span
            className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
              isDark
                ? "bg-[hsl(197,100%,47%)]/10 text-[hsl(197,100%,47%)] border border-[hsl(197,100%,47%)]/20"
                : "bg-blue-50 text-blue-600 border border-blue-100"
            }`}
          >
            {t(ins.badge, lang)}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold font-display mb-4">
            {t(ins.title, lang)}
            <span className="text-[hsl(197,100%,47%)]">
              {t(ins.titleHighlight, lang)}
            </span>
          </h1>
          <p
            className={`text-lg max-w-2xl mx-auto ${
              isDark ? "text-[hsl(215,20%,65%)]" : "text-gray-600"
            }`}
          >
            {t(ins.subtitle, lang)}
          </p>
        </div>

        {/* Articles grid */}
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={localizedPath("insightsArticle", lang, article.slug)}
                className={`group rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${
                  isDark
                    ? "bg-[hsl(222,47%,8%)] border-[hsl(222,30%,18%)] hover:border-[hsl(197,100%,47%)]/40"
                    : "bg-white border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
                }`}
              >
                <div className={`h-48 relative overflow-hidden bg-gradient-to-br ${article.coverGradient} flex items-center justify-center`}>
                  <img
                    src={article.coverImage}
                    alt={article.title[lang]}
                    className="h-24 w-24 object-contain transition-transform duration-300 group-hover:scale-110 drop-shadow-lg"
                    loading="lazy"
                    width={96}
                    height={96}
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        isDark
                          ? "bg-[hsl(197,100%,47%)]/10 text-[hsl(197,100%,47%)]"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {article.category}
                    </span>
                    <span
                      className={`text-xs flex items-center gap-1 ${
                        isDark ? "text-[hsl(215,20%,65%)]" : "text-muted-foreground"
                      }`}
                    >
                      <Clock className="h-3 w-3" />
                      {article.readTime}
                    </span>
                    <span
                      className={`text-xs ${
                        isDark ? "text-[hsl(215,20%,65%)]" : "text-muted-foreground"
                      }`}
                    >
                      {new Date(article.date).toLocaleDateString(lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR")}
                    </span>
                  </div>

                  <h2
                    className={`text-xl font-bold font-display mb-2 transition-colors ${
                      isDark
                        ? "group-hover:text-[hsl(197,100%,47%)]"
                        : "group-hover:text-blue-600"
                    }`}
                  >
                    {article.title[lang]}
                  </h2>
                  <p
                    className={`text-sm mb-4 line-clamp-2 ${
                      isDark ? "text-[hsl(215,20%,65%)]" : "text-gray-600"
                    }`}
                  >
                    {article.excerpt[lang]}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {article.tags[lang].slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                          isDark
                            ? "bg-[hsl(222,47%,12%)] text-[hsl(215,20%,65%)]"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <Tag className="h-2.5 w-2.5" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  <span className="text-[hsl(197,100%,47%)] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t(ins.readTitle, lang)} {article.title[lang]}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Insights;
